import { get, writable } from "svelte/store";
import { api, ApiError, type CreateServerInput, type GameServer } from "@/api";
import { DEFAULT_SERVER_LIMIT, SERVER_SLOT_COST, SERVER_START_COST } from "@/config";
import { token as tokenStore } from "@/stores/session";

export interface Feedback {
  kind: "success" | "error";
  text: string;
  code?: string;
  serverId?: string;
}

export const balance = writable(0);
export const serverLimit = writable(DEFAULT_SERVER_LIMIT);
export const servers = writable<GameServer[]>([]);
export const dailyBonusClaimedToday = writable(false);
export const nodeCapacityAvailable = writable<boolean | null>(null);
export const initialLoading = writable(true);
export const busyAction = writable<string | null>(null);
export const serverActions = writable<Record<string, boolean>>({});
export const feedback = writable<Feedback | null>(null);

let generation = 0;
let accountRevision = 0;
const invalidateResources: Array<() => void> = [];
let capacityExpiry: ReturnType<typeof setTimeout> | null = null;
function clearCapacityHint(): void {
  if (capacityExpiry !== null) {
    clearTimeout(capacityExpiry);
  }
  capacityExpiry = null;
  nodeCapacityAvailable.set(null);
}
const cancelled = () => new DOMException("Запрос отменён", "AbortError");

/** Shared transport ownership is independent of each view's cancellation signal. */
function resource<T>(
  request: (token: string, signal: AbortSignal) => Promise<T>,
  publish: (value: T) => void,
  failed?: () => void,
): (signal?: AbortSignal) => Promise<void> {
  interface Waiter {
    resolve: () => void;
    reject: (error: unknown) => void;
    signal?: AbortSignal;
    abort: () => void;
  }
  interface Job {
    token: string;
    generation: number;
    controller: AbortController;
    waiters: Set<Waiter>;
    dirty: boolean;
  }
  let active: Job | null = null;
  let queued: Job | null = null;
  const current = (job: Job) =>
    job.generation === generation &&
    job.token === get(tokenStore) &&
    !job.controller.signal.aborted;
  const settle = (job: Job, error?: unknown) => {
    for (const waiter of job.waiters) {
      waiter.signal?.removeEventListener("abort", waiter.abort);
      if (error !== undefined) {
        waiter.reject(error);
      } else {
        waiter.resolve();
      }
    }
    job.waiters.clear();
  };
  const run = async (job: Job) => {
    active = job;
    let failure: unknown;
    try {
      do {
        job.dirty = false;
        failure = undefined;
        try {
          const value = await request(job.token, job.controller.signal);
          if (current(job) && !job.dirty) {
            publish(value);
          }
        } catch (error) {
          failure = error;
          if (current(job) && !job.dirty) {
            failed?.();
          }
        }
      } while (current(job) && job.dirty && job.waiters.size > 0);
      settle(job, current(job) ? failure : cancelled());
    } finally {
      // Keep physical singleflight until the underlying request actually settles.
      active = null;
      const next = queued;
      queued = null;
      if (next) {
        void run(next);
      }
    }
  };
  invalidateResources.push(() => {
    if (active) {
      active.controller.abort();
      settle(active, cancelled());
    }
    if (queued) {
      queued.controller.abort();
      settle(queued, cancelled());
      queued = null;
    }
  });
  return async (signal) => {
    if (signal?.aborted) {
      return Promise.reject(cancelled());
    }
    const token = requireToken();
    let job: Job;
    if (active && current(active)) {
      job = active;
      job.dirty = true;
    } else if (queued && current(queued)) {
      job = queued;
    } else {
      job = {
        token,
        generation,
        controller: new AbortController(),
        waiters: new Set(),
        dirty: false,
      };
      if (active) {
        queued = job;
      }
    }
    return new Promise<void>((resolve, reject) => {
      const waiter: Waiter = {
        resolve,
        reject,
        signal,
        abort() {
          job.waiters.delete(waiter);
          signal?.removeEventListener("abort", waiter.abort);
          reject(cancelled());
          if (job.waiters.size === 0) {
            job.controller.abort();
            if (queued === job) {
              queued = null;
            }
          }
        },
      };
      job.waiters.add(waiter);
      signal?.addEventListener("abort", waiter.abort, { once: true });
      if (!active) {
        void run(job);
      }
    });
  };
}

function isToday(timestamp: string | null): boolean {
  return timestamp?.slice(0, 10) === new Date().toISOString().slice(0, 10);
}

export function resetDashboard(): void {
  generation++;
  for (const invalidate of invalidateResources) {
    invalidate();
  }
  balance.set(0);
  serverLimit.set(DEFAULT_SERVER_LIMIT);
  servers.set([]);
  dailyBonusClaimedToday.set(false);
  clearCapacityHint();
  initialLoading.set(true);
  busyAction.set(null);
  serverActions.set({});
  feedback.set(null);
}

function requireToken(): string {
  const current = get(tokenStore);
  if (!current) {
    throw new Error("нет активной сессии");
  }
  return current;
}

export const refreshAccount = resource(
  async (token, signal) => {
    const revision = accountRevision;
    const credits = await api.balance(token, signal);
    return { credits, revision };
  },
  ({ credits, revision }) => {
    // A confirmed credit mutation is newer than a read started before it.
    if (revision !== accountRevision) {
      return;
    }
    balance.set(credits.balance);
    serverLimit.set(credits.serverLimit);
    dailyBonusClaimedToday.set(isToday(credits.lastDailyBonusAt));
  },
);
export const refreshServers = resource(
  (token, signal) => api.servers(token, signal),
  (list) => {
    servers.set(list);
    const notice = get(feedback);
    if (notice?.code !== "billing_settlement_pending" || !notice.serverId) {
      return;
    }
    const refreshed = list.find((server) => server.id === notice.serverId);
    if (refreshed?.billingSettlementPending === false) {
      feedback.set(null);
    }
  },
);
export const refreshCapacity = resource(
  (token, signal) => api.capacity(token, signal),
  (capacity) => {
    clearCapacityHint();
    const ageMs = Math.max(capacity.ageMs, Date.now() - Date.parse(capacity.capturedAt));
    const hasCapacityObservation =
      capacity.stale === false && typeof capacity.available === "boolean";
    const valid = hasCapacityObservation && Number.isFinite(ageMs) && ageMs >= 0 && ageMs < 7_000;
    if (valid) {
      nodeCapacityAvailable.set(capacity.available);
      capacityExpiry = setTimeout(clearCapacityHint, 7_000 - ageMs);
    }
  },
  clearCapacityHint,
);

export async function refreshInitial(
  signal?: AbortSignal,
  options: { includeCapacity?: boolean } = {},
): Promise<void> {
  requireToken();
  const requests = [refreshAccount(signal), refreshServers(signal)];
  if (options.includeCapacity) {
    requests.push(refreshCapacity(signal));
  }
  const results = await Promise.allSettled(requests);
  const failure = results.find((result) => result.status === "rejected");
  if (failure?.status === "rejected") {
    throw failure.reason;
  }
}

/** Routine account refresh never performs a fleet inventory request. */
export async function refresh(signal?: AbortSignal): Promise<void> {
  await refreshInitial(signal);
}

function mutationAuthority(): () => boolean {
  const startedGeneration = generation;
  const startedToken = get(tokenStore);
  return () => generation === startedGeneration && get(tokenStore) === startedToken;
}

export async function claimDailyBonus(): Promise<void> {
  const isCurrent = mutationAuthority();
  busyAction.set("daily-bonus");
  try {
    const result = await api.dailyBonus(requireToken());
    if (!isCurrent()) {
      return;
    }
    accountRevision++;
    balance.set(result.balance);
    dailyBonusClaimedToday.set(true);
    feedback.set({ kind: "success", text: `Начислено ${result.amount} кредитов` });
  } catch (error) {
    if (isCurrent()) {
      feedback.set({ kind: "error", text: (error as Error).message });
    }
  } finally {
    if (isCurrent()) {
      busyAction.set(null);
    }
  }
}

export async function createServer(input: CreateServerInput): Promise<GameServer | null> {
  const isCurrent = mutationAuthority();
  const currentBalance = get(balance);
  if (currentBalance < SERVER_START_COST) {
    feedback.set({
      kind: "error",
      text: `Недостаточно кредитов: нужно ${SERVER_START_COST}, баланс ${currentBalance}`,
    });
    return null;
  }
  busyAction.set("create-server");
  try {
    const created = await api.createServer(requireToken(), input);
    if (!isCurrent()) {
      return null;
    }
    servers.update((list) => [...list.filter((item) => item.id !== created.id), created]);
    accountRevision++;
    balance.update((value) => Math.max(0, value - SERVER_START_COST));
    await refresh().catch(() => {});
    if (!isCurrent()) {
      return null;
    }
    feedback.set({ kind: "success", text: "Сервер создан и поставлен в очередь" });
    return created;
  } catch (error) {
    if (isCurrent()) {
      feedback.set({ kind: "error", text: (error as Error).message });
    }
    return null;
  } finally {
    if (isCurrent()) {
      busyAction.set(null);
    }
  }
}

export async function purchaseServerSlot(): Promise<boolean> {
  const isCurrent = mutationAuthority();
  const currentBalance = get(balance);
  if (currentBalance < SERVER_SLOT_COST) {
    feedback.set({
      kind: "error",
      text: `Недостаточно кредитов: нужно ${SERVER_SLOT_COST}, баланс ${currentBalance}`,
    });
    return false;
  }
  busyAction.set("server-slot");
  try {
    const result = await api.purchaseServerSlot(requireToken());
    if (!isCurrent()) {
      return false;
    }
    accountRevision++;
    balance.set(result.balance);
    serverLimit.set(result.serverLimit);
    feedback.set({
      kind: "success",
      text: `Дополнительный слот куплен за ${result.cost} кредитов`,
    });
    return true;
  } catch (error) {
    if (isCurrent()) {
      feedback.set({ kind: "error", text: (error as Error).message });
    }
    return false;
  } finally {
    if (isCurrent()) {
      busyAction.set(null);
    }
  }
}

export type ServerAction = "start" | "stop" | "restart" | "kill";

export async function runServerAction(
  server: GameServer | { id: string; hourlyCost?: number },
  action: ServerAction,
): Promise<boolean> {
  const isCurrent = mutationAuthority();
  const actionKey = `${action}:${server.id}`;
  if (get(serverActions)[actionKey]) {
    return false;
  }
  serverActions.update((actions) => ({ ...actions, [actionKey]: true }));
  busyAction.set(actionKey);
  try {
    const current = requireToken();
    if (action === "start") {
      await api.startServer(current, server.id);
    } else if (action === "stop") {
      await api.stopServer(current, server.id);
    } else if (action === "restart") {
      await api.restartServer(current, server.id);
    } else {
      await api.killServer(current, server.id);
    }
    if (!isCurrent()) {
      return false;
    }
    await refresh().catch(() => {});
    if (!isCurrent()) {
      return false;
    }
    feedback.set({
      kind: "success",
      text: {
        start: "Сервер поставлен в очередь запуска",
        stop: "Сервер останавливается",
        restart: "Перезапуск сервера поставлен в очередь",
        kill: "Аварийная остановка сервера выполнена",
      }[action],
    });
    return true;
  } catch (error) {
    if (isCurrent()) {
      feedback.set({
        kind: "error",
        text: (error as Error).message,
        ...(error instanceof ApiError && error.code
          ? { code: error.code, serverId: server.id }
          : {}),
      });
      if (
        action === "start" &&
        error instanceof ApiError &&
        error.code === "billing_settlement_pending"
      ) {
        // A raced admission refusal must also keep list/overview reconciliation active.
        servers.update((list) =>
          list.map((item) =>
            item.id === server.id ? { ...item, billingSettlementPending: true } : item,
          ),
        );
      }
    }
    return false;
  } finally {
    if (isCurrent()) {
      serverActions.update((actions) => {
        const next = { ...actions };
        delete next[actionKey];
        return next;
      });
      busyAction.update((current) =>
        current === actionKey ? (Object.keys(get(serverActions)).at(-1) ?? null) : current,
      );
    }
  }
}

export async function deleteServer(server: GameServer): Promise<boolean> {
  const isCurrent = mutationAuthority();
  busyAction.set(`delete:${server.id}`);
  try {
    await api.deleteServer(requireToken(), server.id);
    if (!isCurrent()) {
      return false;
    }
    await refresh();
    if (!isCurrent()) {
      return false;
    }
    feedback.set({ kind: "success", text: "Сервер поставлен в очередь удаления" });
    return true;
  } catch (error) {
    if (isCurrent()) {
      feedback.set({ kind: "error", text: (error as Error).message });
    }
    return false;
  } finally {
    if (isCurrent()) {
      busyAction.set(null);
    }
  }
}

// Invalidate even if a caller does not use the App shell (logout/account switch).
let dashboardToken = get(tokenStore);
tokenStore.subscribe((next) => {
  if (next !== dashboardToken) {
    dashboardToken = next;
    resetDashboard();
  }
});
